import {
  Button,
  ConfirmModal,
  InputDay,
  InputWrapper,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiClient,
  useApiMutation,
  useInvalidateApiQueries,
  useTeam,
} from '@vrplatform/data';
import { day, type Maybe } from '@vrplatform/utils';
import { Box, Group, Modal, Text } from '@mantine/core';
import { type ReactNode, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getListingDeactivationAction } from '../detail/_components';
import type { ListingData } from '../detail/useListingDetailViewQuery';
import { DrawerInfoCard } from '../../../drawers/_components';
import { UserIcon } from '@vrplatform/icons';
import { submitListingDeactivation } from './ListingDeactivationModal.utils';

type Props = {
  opened: boolean;
  closeModal: () => void;
  listing: {
    id: string;
    deactivation: ListingData['deactivation'] | undefined;
    activeOwnership?: Maybe<ListingData['activeOwnership']>;
    activeRecurringFeePeriods?: ListingData['activeRecurringFeePeriods'];
  };
};

export const ListingDeactivationModal = ({
  listing,
  closeModal,
  opened,
}: Props) => {
  const modalTitle = useMemo(() => {
    if (!listing.deactivation?.status) return '-';

    return (
      getListingDeactivationAction(listing.deactivation.status, () => {})
        ?.label || '-'
    );
  }, [listing.deactivation?.status]);

  if (listing.deactivation?.status === 'active') {
    return (
      <Edit
        closeModal={closeModal}
        opened={opened}
        title={modalTitle}
        description="This listing will be deactivated, set the date from when this listing should be deactivated."
        startDateLabel="Deactivate from"
        endDateLabel="Deactivate until"
        listing={listing}
      />
    );
  }

  if (
    listing.deactivation?.status === 'disabled-ending' ||
    listing.deactivation?.status === 'disabled'
  ) {
    return (
      <Edit
        closeModal={closeModal}
        opened={opened}
        title={modalTitle}
        description="Re-activating the listing will allow you to set ownerships, fees and create statements."
        endDateLabel="Re-activate from"
        listing={listing}
      />
    );
  }

  if (listing.deactivation?.status === 'upcoming') {
    return (
      <Cancel
        periodId={listing.deactivation?.period.id}
        closeModal={closeModal}
        opened={opened}
      />
    );
  }

  return null;
};

type FormInputs = {
  startAt: string;
  endAt: string | null;
};

const Cancel = ({
  periodId,
  closeModal,
  opened,
}: {
  opened: boolean;
  closeModal: () => void;
  periodId: string;
}) => {
  const invalidate = useInvalidateApiQueries(['listings']);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'delete',
    '/listings/ownership-periods/{id}'
  );

  const submit = useCallback(async () => {
    await mutateAsync({
      params: {
        path: {
          id: periodId,
        },
        query: {
          onLocked: 'error',
        },
      },
    })
      .then(() => {
        invalidate();
        closeModal();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to cancel deactivation',
          defaultMessage:
            'Please try again later and contact support if the problem persists.',
        });
      });
  }, [mutateAsync, periodId, invalidate, closeModal]);

  return (
    <ConfirmModal
      closeModal={closeModal}
      opened={opened}
      title="Cancel deactivation"
      subtitle="Are you sure you want to cancel the upcoming deactivation?"
      type="confirm"
      submitTitle="Cancel"
      submitIcon="BroomIcon"
      loading={loading}
      onSubmit={submit}
    />
  );
};

const Edit = ({
  closeModal: cl,
  opened,
  title,
  description,
  startDateLabel,
  endDateLabel,
  submitTitle = 'Submit',
  listing,
}: {
  opened: boolean;
  closeModal: () => void;
  title: string;
  description: string;
  startDateLabel?: string;
  endDateLabel: ReactNode;
  submitTitle?: string;
  listing: Props['listing'];
}) => {
  const [{ statementStartAt }] = useTeam();

  const defaultValues = {
    startAt: listing.deactivation?.period?.startAt ?? undefined,
    endAt: listing.deactivation?.period?.endAt ?? undefined,
  };

  const methods = useForm<FormInputs>({
    defaultValues,
  });

  const resetForm = useCallback(() => {
    methods.reset({
      endAt: null,
      startAt: undefined,
    });
  }, [methods]);

  const submit = useDeactivationSubmit(listing, cl, resetForm);

  const closeModal = () => {
    cl();
    setTimeout(() => {
      methods.reset(defaultValues);
    }, 100);
  };

  const showStartInput = !!startDateLabel;

  const startAtMinDate = statementStartAt ?? undefined;

  return (
    <Modal
      centered
      opened={opened}
      onClose={closeModal}
      title={title}
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Text c="neutral" mb="md">
        {description}
      </Text>

      <ActiveOwnershipSummary activeOwnership={listing.activeOwnership} />

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        {showStartInput && (
          <Controller
            control={methods.control}
            name="startAt"
            rules={{
              required: 'Start date is required',
            }}
            render={({ field, fieldState: { error } }) => {
              const endAt = methods.watch('endAt');

              return (
                <InputWrapper
                  label={startDateLabel}
                  error={error?.message}
                  required
                  mb="md"
                >
                  <InputDay
                    value={field.value ? day(field.value).toDate() : null}
                    onChange={(value) =>
                      field.onChange(value ? day(value).yyyymmdd() : null)
                    }
                    maxDate={
                      endAt ? day(endAt).subtract(1, 'day').toDate() : undefined
                    }
                    minDate={startAtMinDate}
                    placeholder={day().format('LL')}
                    clearable
                    error={!!error}
                  />
                </InputWrapper>
              );
            }}
          />
        )}

        <Controller
          control={methods.control}
          name="endAt"
          rules={{
            validate: (value, formValues) => {
              if (
                value &&
                showStartInput &&
                (day(value).isBefore(day(formValues.startAt), 'day') ||
                  day(value).isSame(day(formValues.startAt), 'day'))
              ) {
                return 'End date must be after start date';
              }

              if (!showStartInput && !value) return 'Please enter a date';

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => {
            const startAt = methods.watch('startAt');

            return (
              <InputWrapper
                label={endDateLabel}
                error={error?.message}
                description={
                  showStartInput
                    ? 'An end date is optional and can be left blank'
                    : undefined
                }
              >
                <InputDay
                  value={field.value ? day(field.value).toDate() : null}
                  onChange={(value) =>
                    field.onChange(value ? day(value).yyyymmdd() : null)
                  }
                  minDate={
                    startAt
                      ? day(startAt)
                          .add(showStartInput ? 1 : 0, 'day')
                          .toDate()
                      : undefined
                  }
                  clearable
                  placeholder={'Forever'}
                  error={!!error}
                />
              </InputWrapper>
            );
          }}
        />

        <Group justify="right" mt="lg">
          {/* {!showStartInput && listing.deactivation.period?.id && (
            <Button
              leftIcon="RefreshCwIcon"
              onClick={() => {}}
              sx={{
                marginRight: 'auto',
              }}
            >
              Reset deactivation
            </Button>
          )} */}
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
          >
            {submitTitle}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

const ActiveOwnershipSummary = ({
  activeOwnership,
}: {
  activeOwnership?: Maybe<ListingData['activeOwnership']>;
}) => {
  if (!activeOwnership) return null;

  const startAt = day(activeOwnership.startAt).format('ll');
  const endAt = activeOwnership.endAt
    ? day(activeOwnership.endAt).format('ll')
    : 'Forever';

  return (
    <DrawerInfoCard
      labelWidth={150}
      rows={[
        {
          icon: UserIcon,
          title: 'Current ownership',
          text: `${startAt} - ${endAt}`,
        },
      ]}
    />
  );
};

function useDeactivationSubmit(
  listing: Props['listing'],
  closeModal: () => void,
  resetForm: () => void
) {
  const current = listing.deactivation;
  const $api = useApiClient();

  const invalidate = useInvalidateApiQueries(['listings']);

  const { mutateAsync: update } = useApiMutation(
    'put',
    '/listings/ownership-periods/{id}'
  );
  const { mutateAsync: cancel } = useApiMutation(
    'delete',
    '/listings/ownership-periods/{id}'
  );

  return useCallback(
    async (data: FormInputs) => {
      try {
        if (current?.status === 'active') {
          await submitListingDeactivation({
            $api,
            listing: {
              id: listing.id,
              activeOwnership: listing.activeOwnership
                ? {
                    id: listing.activeOwnership.id,
                    startAt: listing.activeOwnership.startAt,
                  }
                : undefined,
              activeRecurringFeePeriods: listing.activeRecurringFeePeriods?.map(
                (period) => ({
                  id: period.id,
                  startAt: period.startAt,
                })
              ),
            },
            startAt: data.startAt,
            endAt: data.endAt,
          });
        }

        if (
          current?.status === 'disabled' ||
          current?.status === 'disabled-ending'
        ) {
          if (data.endAt === data.startAt) {
            await cancel({
              params: {
                path: {
                  id: current?.period.id,
                },
                query: {
                  onLocked: 'error',
                },
              },
            });
            resetForm();
          } else {
            await update({
              params: {
                path: {
                  id: current?.period.id,
                },
              },
              body: {
                endAt: data.endAt,
                onConflict: 'updateExisting',
              },
            });
          }
        }

        invalidate();
        closeModal();
      } catch (error) {
        showApiErrorNotification({
          error,
          title: 'Failed to update listing',
          defaultMessage:
            'Please try again later and contact support if the problem persists.',
        });
      }
    },
    [
      $api,
      invalidate,
      update,
      cancel,
      current?.period?.id,
      current?.status,
      closeModal,
      listing.id,
      listing.activeOwnership,
      listing.activeRecurringFeePeriods,
      resetForm,
    ]
  );
}
